import type { ReactNode } from 'react';

import { formatPreviewDate } from './previewModel';
import type { PreviewCategory, PreviewPost } from './previewModel';

/** Ảnh của bài trong bản xem trước, hoặc ô giữ chỗ khi bài chưa có ảnh bìa. */
export function PreviewImage({ url, alt }: { readonly url: string | null; readonly alt: string }): ReactNode {
  return url === null ? <div className="mv-placeholder">MVA LAB</div> : <img src={url} alt={alt} loading="lazy" />;
}

/** Nhãn tình trạng trên ảnh — chỉ bài nháp và bài hẹn giờ mới có, vì khách không thấy những bài đó. */
export function PreviewStatusBadge({ post }: { readonly post: PreviewPost }): ReactNode {
  if (post.status === 'draft') {
    return <span className="mv-status draft">Nháp</span>;
  }

  return post.status === 'scheduled' ? <span className="mv-status scheduled">Hẹn giờ</span> : null;
}

interface PreviewSidebarProps {
  /** Tổng số bài — gồm cả bài chưa phân loại, nên không cộng từ các danh mục được. */
  readonly totalCount: number;
  readonly categories: readonly PreviewCategory[];
  readonly recent: readonly PreviewPost[];
  readonly activeCategory: string | null;
  readonly onSelectCategory: (slug: string | null) => void;
  readonly onOpen: (post: PreviewPost) => void;
}

/** Cột bên phải của trang blog: danh mục, bài mới nhất và ô kêu gọi liên hệ. */
export function PreviewSidebar({
  totalCount,
  categories,
  recent,
  activeCategory,
  onSelectCategory,
  onOpen,
}: PreviewSidebarProps): ReactNode {
  return (
    <aside>
      <div className="mv-side-box">
        <h3 className="mv-side-title">Danh mục</h3>
        <div
          className={`mv-cat-item${activeCategory === null ? ' active' : ''}`}
          onClick={() => { onSelectCategory(null); }}
        >
          <span>Tất cả bài viết</span>
          <span className="mv-cat-count">{totalCount.toString()}</span>
        </div>
        {categories.map((category) => (
          <div
            key={category.slug}
            className={`mv-cat-item${activeCategory === category.slug ? ' active' : ''}`}
            onClick={() => { onSelectCategory(category.slug); }}
          >
            <span>{category.name}</span>
            <span className="mv-cat-count">{category.count.toString()}</span>
          </div>
        ))}
      </div>

      {recent.length === 0 ? null : (
        <div className="mv-side-box">
          <h3 className="mv-side-title">Bài viết mới nhất</h3>
          {recent.map((post) => (
            <div key={post.id} className="mv-recent" onClick={() => { onOpen(post); }}>
              <div className="mv-recent-img">
                <PreviewImage url={post.coverImageUrl} alt={post.coverImageAlt} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="mv-recent-title">{post.title}</div>
                <div className="mv-recent-date">{formatPreviewDate(post.date)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mv-cta">
        <h4>Cần tư vấn giải pháp?</h4>
        <p>Đội ngũ kỹ sư MVA Lab sẵn sàng khảo sát và đề xuất giải pháp phù hợp cho nhà máy của bạn.</p>
        <span>Liên hệ ngay</span>
      </div>
    </aside>
  );
}
